import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CircularProgress, Box } from "@mui/material";
import { silver } from "../daiService";
import api from "../api";
import { toDashboardView, STATUS_CHIP_LABEL, type DashboardRow, type DashboardView } from "./daiAdapters";
import { STATUS_OPTIONS, CRITICAL_OPTIONS, TRIGGER_OPTIONS, marketOptions } from "../constants";
import DaiCustomSelectDropdown from "../components/DaiCustomSelectDropdown";
import DialogModal from "../components/DialogModalDai";
import StatusChip from "../components/StatusChip";
import { SummaryCard } from "../../Common/SummaryCards";
import Header from "../components/Header";
import Nav from "../components/Nav";
import Pagination from "../components/Pagination";
import DetailPanel from "../components/DetailPanel";
import ApplicationToggle from "../components/ApplicationToggle";
import HourlyView from "../components/HourlyView";
import DateInput from "../components/DateInput";
import { hasApplicationTag } from "../utils/applicationTags";
import { sortItems, useSortState, SortTh, useColumnVisibility, ColumnMenu } from "../hooks/tableTools";

// Clean execution-state label, independent of SLA timing.
const RUN_STATUS_MAP: Record<string, string> = {
  scheduled: "Not Started", missed: "Not Started", delayed: "Not Started",
  in_progress: "Running", delayed_running: "Running",
  success: "Completed", delayed_ok: "Completed", recovered: "Completed", missed_sla: "Completed",
  failed: "Failed", cancelled: "Cancelled", unknown: "Unknown",
};

// delayed_ok and delayed_running count in the Delayed bucket (matches backend summary)
const COMPLETED_STATUSES = ["success", "delayed", "missed_sla"];

// Ported from app.js EXACTLY.
function matchesStatusBucket(item: any, bucket: string): boolean {
  if (bucket === "completed") return COMPLETED_STATUSES.includes(item.status);
  return item.status === bucket;
}

const MARKETS = ["FR-BNLX", "EUR", "FLNA", "PBNA", "PFNA", "APAC", "EAMEA", "ESSA", "IBERIA", "PMF", "TR/TURKEY", "WB"];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}
function daysAgoIso(n: number) {
  return new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);
}
function truncate(str: string | null | undefined, maxLen: number): string {
  if (!str) return "";
  return str.length > maxLen ? str.slice(0, maxLen) + "..." : str;
}

interface SilverColumn {
  key: string;
  label: string;
  sortKey: string | null;
  render?: (item: DashboardRow) => React.ReactNode;
}

const COLUMNS: SilverColumn[] = [
  { key: "row", label: "#", sortKey: null },
  { key: "market", label: "Market", sortKey: "market", render: (item) => item.market },
  { key: "gdo", label: "GDO", sortKey: "gdo", render: (item) => item.gdo || "-" },
  { key: "data_asset", label: "Data Asset", sortKey: "data_asset", render: (item) => <span title={item.data_asset}>{truncate(item.data_asset, 40)}</span> },
  { key: "expected_time", label: "Historical Average Start (UTC)", sortKey: "expected_time", render: (item) => (
    <>
      <span className="exp-start">{item.expected_time}</span>
      {!item.hist_avg_start && item.csv_expected && <span className="no-hist-badge" title="No history yet — showing CSV expected time">CSV</span>}
    </>
  ) },
  { key: "hist_avg_end", label: "Historical Average End (UTC)", sortKey: "hist_avg_end", render: (item) => (
    item.hist_avg_end ? <span className="exp-end-badge" title="Hist avg end">{item.hist_avg_end} UTC</span> : <span style={{ color: "#555" }}>&mdash;</span>
  ) },
  { key: "actual_start", label: "Act Start (UTC)", sortKey: "actual_start", render: (item) => (item.actual_start ? `${item.actual_start} UTC` : "\u2014") },
  { key: "actual_end", label: "Act End (UTC)", sortKey: "actual_end", render: (item) => (item.actual_end ? `${item.actual_end} UTC` : "\u2014") },
  { key: "run_count", label: "Runs", sortKey: "run_count", render: (item) => (item.run_count > 0 ? <>{item.run_count}{item.failed_count > 0 && <span className="run-fail-count"> ({item.failed_count}&#10005;)</span>}</> : "-") },
  { key: "duration", label: "Duration", sortKey: "duration", render: (item) => item.duration || "-" },
  { key: "trigger_type", label: "Trigger", sortKey: "trigger_type", render: (item) => item.trigger_type },
  { key: "critical", label: "Critical", sortKey: "critical", render: (item) => (item.critical ? <span className="critical-badge">CRITICAL</span> : "-") },
  { key: "status", label: "SLA Status", sortKey: "status", render: (item) => (
    <StatusChip status={STATUS_CHIP_LABEL[item.status] || item.status} />
  ) },
  { key: "run_status", label: "Run Status", sortKey: null, render: (item) => RUN_STATUS_MAP[item.status] || item.status },
];
const COLUMN_KEYS = COLUMNS.map((c) => c.key);

const REPORT_STATUS_COLOR: Record<string, string> = {
  success: "#1e8e3e", on_time: "#1e8e3e", delayed_ok: "#c96a11", delayed: "#c96a11", delayed_running: "#c96a11",
  missed: "#c0392b", failed: "#c0392b", in_progress: "#2f7bc4", recovered: "#5b4dc4", scheduled: "#64748b", unknown: "#64748b",
};
const REPORT_STATUS_LABEL: Record<string, string> = {
  success: "On Time", on_time: "On Time", delayed_ok: "Late (done)", delayed: "Delayed", delayed_running: "Running Late",
  missed: "Missed", failed: "Failed", in_progress: "Running", recovered: "Recovered", scheduled: "Scheduled", unknown: "Unknown",
};

function ReportSummaryBar({ reportLoading, reportError, reportData, reportItems, reportSummary }: any) {
  if (reportLoading) return <>Loading…</>;
  if (reportError) return <span style={{ color: "#e74c3c" }}>{reportError}</span>;
  if (!reportData) return null;
  const parts: Array<[number, string, string]> = [
    [reportSummary.onTime, "#1e8e3e", `✓ On Time: ${reportSummary.onTime}`],
    [reportSummary.late, "#c96a11", `⚠ Late(done): ${reportSummary.late}`],
    [reportSummary.runLate, "#c96a11", `Running Late: ${reportSummary.runLate}`],
    [reportSummary.missed, "#c0392b", `✕ Missed: ${reportSummary.missed}`],
    [reportSummary.failed, "#c0392b", `✕ Failed: ${reportSummary.failed}`],
    [reportSummary.running, "#2f7bc4", `Running: ${reportSummary.running}`],
    [reportSummary.recovered, "#5b4dc4", `Recovered: ${reportSummary.recovered}`],
    [reportSummary.scheduled, "#64748b", `Scheduled: ${reportSummary.scheduled}`],
  ];
  return (
    <>
      <b>Showing:</b> {reportItems.length} of {(reportData.items || []).length}
      {parts.map(([count, color, text], i) => (count ? <span key={i} style={{ color }}> &nbsp;|&nbsp; {text}</span> : null))}
    </>
  );
}

function ReportTableRows({ reportLoading, reportItems }: any) {
  if (reportLoading) return <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#64748b" }}>Loading…</td></tr>;
  if (reportItems.length === 0) return <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#64748b" }}>No data for this date</td></tr>;
  return (
    <>
      {reportItems.map((row: any, i: number) => (
        <tr key={i} style={{ borderBottom: "1px solid #dde4ec" }}>
          <td style={{ padding: "6px 10px", color: REPORT_STATUS_COLOR[row.status] || "#64748b", fontWeight: 600 }}>{REPORT_STATUS_LABEL[row.status] || row.status}</td>
          <td style={{ padding: "6px 10px", color: "#1f2d3d" }}>{row.data_asset || ""}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{row.market || ""}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{row.gdo || ""}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{row.expected_start || row.expected_time || ""}</td>
          <td style={{ padding: "6px 10px", color: "#475569" }}>{row.expected_end || "—"}</td>
          <td style={{ padding: "6px 10px", color: row.actual_start && row.actual_start !== "-" ? "#1e8e3e" : "#94a3b8" }}>{row.actual_start || "—"}</td>
          <td style={{ padding: "6px 10px", color: row.actual_end && row.actual_end !== "-" ? "#1e8e3e" : "#94a3b8" }}>{row.actual_end || "—"}</td>
          <td style={{ padding: "6px 10px", color: "#475569", textAlign: "center" }}>{row.run_count || 0}</td>
          <td style={{ padding: "6px 10px", color: row.critical ? "#c96a11" : "#94a3b8" }}>{row.critical ? "★" : ""}</td>
        </tr>
      ))}
    </>
  );
}

function PipelineTableBody({ view, isRefreshing, pageItems, visibleColumns, page, rowsPerPage, onRowClick }: any) {
  if (!view || isRefreshing) {
    return (
      <tr><td colSpan={visibleColumns.length} className="loading" style={{ textAlign: "center", padding: "30px" }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
          <CircularProgress size={24} />
          <span>{isRefreshing ? "Refreshing data..." : "Loading pipeline data..."}</span>
        </Box>
      </td></tr>
    );
  }
  if (pageItems.length === 0) {
    return <tr><td colSpan={visibleColumns.length} className="loading">No pipelines match the current filters</td></tr>;
  }
  return (
    <>
      {pageItems.map((item: any, idx: number) => (
        <tr key={idx} className={`row-${item.status}`} onClick={() => onRowClick(item.data_asset)}>
          {visibleColumns.map((c: any) => (
            <td key={c.key}>{c.key === "row" ? (page - 1) * rowsPerPage + idx + 1 : c.render!(item)}</td>
          ))}
        </tr>
      ))}
    </>
  );
}

// Extracted helper component to reduce cognitive complexity
function SummaryBar({ counts, statusFilter, toggleCardFilter, view, isRefreshing }: any) {
  const subCards = [
    { status: "success", label: "On Time", count: counts.success },
    { status: "delayed", label: "Delayed Start", count: counts.delayed },
    { status: "missed_sla", label: "Missed SLA", count: counts.missed_sla },
  ];
  return (
    <div className="summary-bar" id="summary-bar">
      <SummaryCard status="" label="All Pipelines" count={counts.all} isActive={statusFilter === ""} onClick={() => toggleCardFilter("")} isLoading={!view || isRefreshing} />
      <div className="summary-card-group">
        <SummaryCard status="completed" label="Completed" count={counts.completed} isActive={statusFilter === "completed"} onClick={() => toggleCardFilter("completed")} isLoading={!view || isRefreshing} />
        <div className="summary-card-sub-stack">
          {subCards.map((c) => (
            <SummaryCard key={c.status} status={c.status} label={c.label} count={c.count} isActive={statusFilter === c.status} onClick={() => toggleCardFilter(c.status)} isLoading={!view || isRefreshing} />
          ))}
        </div>
      </div>
      <SummaryCard status="inprog" label="In Progress" count={counts.inprog} isActive={statusFilter === "inprog"} onClick={() => toggleCardFilter("inprog")} isLoading={!view || isRefreshing} />
      <SummaryCard status="missed" label="Missed" count={counts.missed} isActive={statusFilter === "missed"} onClick={() => toggleCardFilter("missed")} isLoading={!view || isRefreshing} />
      <SummaryCard status="failed" label="Failed" count={counts.failed} isActive={statusFilter === "failed"} onClick={() => toggleCardFilter("failed")} isLoading={!view || isRefreshing} />
      <SummaryCard status="scheduled" label="Scheduled" count={counts.scheduled} isActive={statusFilter === "scheduled"} onClick={() => toggleCardFilter("scheduled")} isLoading={!view || isRefreshing} />
    </div>
  );
}

// Extracted helper component for table view
function TableViewPanel({
  visibleColumns, sortKey, sortDir, onSort, error, view, isRefreshing, pageItems, page, rowsPerPage, setOpenAsset,
  filteredItems, setPage, setRowsPerPage, search, setSearch, appFilter, setAppFilter, statusFilter, setStatusFilter,
  criticalFilter, setCriticalFilter, marketFilter, setMarketFilter, triggerFilter, setTriggerFilter,
  date, setDate, openReport, clearSort, clearAllFilters, hidden, toggle, selectAll, allChecked
}: any) {
  return (
    <div id="tv-panel" style={{ display: "block" }}>
      <div className="controls">
        <input type="text" placeholder="Search pipelines..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <ApplicationToggle value={appFilter} onChange={setAppFilter} />
        <DaiCustomSelectDropdown options={STATUS_OPTIONS} value={statusFilter} handleChange={(e) => setStatusFilter(e.target.value)} noSelectionLabel="Select Status" />
        <DaiCustomSelectDropdown options={CRITICAL_OPTIONS} value={criticalFilter} handleChange={(e) => setCriticalFilter(e.target.value)} noSelectionLabel="Select Priority" />
        <DaiCustomSelectDropdown options={marketOptions(MARKETS)} value={marketFilter} handleChange={(e) => setMarketFilter(e.target.value)} noSelectionLabel="Select Market" />
        <DaiCustomSelectDropdown options={TRIGGER_OPTIONS} value={triggerFilter} handleChange={(e) => setTriggerFilter(e.target.value)} noSelectionLabel="Select Trigger" />
        <div className="controls-actions">
          <DateInput value={date} min={daysAgoIso(7)} max={todayIso()} onChange={(e) => setDate(e.target.value || todayIso())} />
          <button type="button" className="btn-sm btn-report" onClick={openReport}>&#9660; Status Report</button>
          <ColumnMenu columns={COLUMNS} hidden={hidden} toggle={toggle} selectAll={selectAll} allChecked={allChecked} />
          <button type="button" className="btn-sm" onClick={clearSort}>&#10005; Clear Sort</button>
          <button type="button" className="btn-sm" onClick={clearAllFilters}>&#10005; Clear Filters</button>
        </div>
      </div>
      {error && <div style={{ color: "#c0392b", padding: "6px 20px" }}>{error}</div>}
      <div className="table-container">
        <table id="pipeline-table">
          <thead>
            <tr>
              {visibleColumns.map((c: any) => (
                <SortTh key={c.key} columnKey={c.sortKey} sortKey={sortKey} sortDir={sortDir} onSort={onSort}>{c.label}</SortTh>
              ))}
            </tr>
          </thead>
          <tbody id="pipeline-tbody">
            {(!view || isRefreshing) ? (
              <tr><td colSpan={visibleColumns.length} className="loading" style={{ textAlign: "center", padding: "30px" }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                  <CircularProgress size={24} />
                  <span>{isRefreshing ? "Refreshing data..." : "Loading pipeline data..."}</span>
                </Box>
              </td></tr>
            ) : pageItems.length === 0 ? (
              <tr><td colSpan={visibleColumns.length} className="loading">No pipelines match the current filters</td></tr>
            ) : (
              pageItems.map((item: any, idx: number) => (
                <tr key={idx} className={`row-${item.status}`} onClick={() => setOpenAsset(item.data_asset)}>
                  {visibleColumns.map((c: any) => (
                    <td key={c.key}>{c.key === "row" ? (page - 1) * rowsPerPage + idx + 1 : c.render!(item)}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination total={filteredItems.length} page={page} rowsPerPage={rowsPerPage} onPageChange={setPage} onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(1); }} />
    </div>
  );
}

// Extracted helper component for report modal content
function ReportModalContent({ reportLoading, reportError, reportData, reportItems, reportSummary, downloadReportCsv }: any) {
  return (
    <>
      <div style={{ padding: "10px 20px", fontSize: ".8rem", color: "#64748b", borderBottom: "1px solid #dde4ec", background: "var(--panel-bg)" }}>
        {reportLoading ? "Loading…" : reportError ? (
          <span style={{ color: "#e74c3c" }}>{reportError}</span>
        ) : reportData ? (
          <>
            <b>Showing:</b> {reportItems.length} of {(reportData.items || []).length}
            {reportSummary.onTime ? <span style={{ color: "#1e8e3e" }}> &nbsp;|&nbsp; ✓ On Time: {reportSummary.onTime}</span> : null}
            {reportSummary.late ? <span style={{ color: "#c96a11" }}> &nbsp;|&nbsp; ⚠ Late(done): {reportSummary.late}</span> : null}
            {reportSummary.runLate ? <span style={{ color: "#c96a11" }}> &nbsp;|&nbsp; Running Late: {reportSummary.runLate}</span> : null}
            {reportSummary.missed ? <span style={{ color: "#c0392b" }}> &nbsp;|&nbsp; ✕ Missed: {reportSummary.missed}</span> : null}
            {reportSummary.failed ? <span style={{ color: "#c0392b" }}> &nbsp;|&nbsp; ✕ Failed: {reportSummary.failed}</span> : null}
            {reportSummary.running ? <span style={{ color: "#2f7bc4" }}> &nbsp;|&nbsp; Running: {reportSummary.running}</span> : null}
            {reportSummary.recovered ? <span style={{ color: "#5b4dc4" }}> &nbsp;|&nbsp; Recovered: {reportSummary.recovered}</span> : null}
            {reportSummary.scheduled ? <span style={{ color: "#64748b" }}> &nbsp;|&nbsp; Scheduled: {reportSummary.scheduled}</span> : null}
          </>
        ) : null}
      </div>
      <div style={{ overflowX: "auto", maxHeight: "60vh", overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: ".8rem", background: "var(--panel-bg)" }}>
          <thead style={{ position: "sticky", top: 0, background: "var(--navy-800)" }}>
            <tr>
              {["Status", "Data Asset", "Market", "GDO", "Exp Start UTC", "Exp End UTC", "Act Start UTC", "Act End UTC", "Runs", "Critical"].map((h) => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: "#ffffff", borderBottom: "1px solid #dde4ec" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reportLoading ? (
              <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#64748b" }}>Loading…</td></tr>
            ) : reportItems.length === 0 ? (
              <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#64748b" }}>No data for this date</td></tr>
            ) : (
              <ReportTableRows reportLoading={false} reportItems={reportItems} />
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default function SilverDashboard() {
  const [view, setView] = useState<DashboardView | null>(null);
  const [date, setDate] = useState(todayIso());
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [criticalFilter, setCriticalFilter] = useState("");
  const [marketFilter, setMarketFilter] = useState("");
  const [triggerFilter, setTriggerFilter] = useState("");
  const [appFilter, setAppFilter] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAsset, setOpenAsset] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [viewMode, setViewMode] = useState<"table" | "hourly">("table");
  const [reportOpen, setReportOpen] = useState(false);
  const [reportData, setReportData] = useState<any>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { sortKey, sortDir, onSort, clearSort } = useSortState();
  const { hidden, toggle, selectAll, allChecked } = useColumnVisibility(COLUMN_KEYS);

  const fetchData = useCallback(async (d: string) => {
    const { ok, status, data, error: err } = await silver.getStatus(d);
    if (!ok) {
      if (status === 401 || status === 403) {
        setError("⚠️ Authentication required. Please log in at /userLogin");
        return;
      }
      setError("Failed to fetch data: " + err);
      return;
    }
    setView(toDashboardView((data as any)?.date ?? d, (data as any)?.items, { last_refresh: (data as any)?.last_refresh, db_live: (data as any)?.db_live }));
    setError("");
  }, []);

  useEffect(() => {
    fetchData(date);
    const id = setInterval(() => fetchData(date), 900000);
    return () => clearInterval(id);
  }, [date, fetchData]);

  const baseItems = useMemo(() => {
    if (!view || !view.items) return [];
    const term = search.toLowerCase();
    return view.items.filter((item) => {
      //fix the bug
      if (term && !item?.data_asset?.toLowerCase().includes(term) && !item?.gdo?.toLowerCase().includes(term)) return false;
      if (criticalFilter === "true" && !item.critical) return false;
      if (criticalFilter === "false" && item.critical) return false;
      if (marketFilter && item.market !== marketFilter) return false;
      if (triggerFilter && item.trigger_type !== triggerFilter) return false;
      if (!hasApplicationTag(item.application, appFilter)) return false;
      return true;
    });
  }, [view, search, criticalFilter, marketFilter, triggerFilter, appFilter]);

  const counts = useMemo(() => {
    const buckets = ["completed", "success", "delayed", "missed_sla", "inprog", "missed", "failed", "scheduled"];
    const c: Record<string, number> = { all: baseItems.length };
    for (const b of buckets) c[b] = baseItems.filter((i) => matchesStatusBucket(i, b)).length;
    return c;
  }, [baseItems]);

  const filteredItems = useMemo(() => (!statusFilter ? baseItems : baseItems.filter((i) => matchesStatusBucket(i, statusFilter))), [baseItems, statusFilter]);

  const sortedItems = useMemo(() => sortItems(filteredItems, sortKey, sortDir), [filteredItems, sortKey, sortDir]);

  useEffect(() => setPage(1), [search, statusFilter, criticalFilter, marketFilter, triggerFilter, appFilter]);

  const pageItems = sortedItems.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const visibleColumns = COLUMNS.filter((c) => !hidden.has(c.key));

  const toggleCardFilter = useCallback((status: string) => {
    setStatusFilter((cur) => (cur === status ? "" : status));
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("");
    setCriticalFilter("");
    setMarketFilter("");
    setTriggerFilter("");
    setAppFilter("");
    const changed = date !== todayIso();
    setDate(todayIso());
    if (changed) fetchData(todayIso());
  }, [date, fetchData]);

  const openReport = useCallback(async () => {
    setReportOpen(true);
    setReportLoading(true);
    setReportError("");
    setReportData(null);
    const { ok, data, error: err } = await silver.getReport(date);
    setReportLoading(false);
    if (!ok) {
      setReportError(err || "");
      return;
    }
    setReportData(data);
  }, [date]);

  const reportItems = useMemo(() => {
    if (!reportData) return [];
    let items: any[] = reportData.items || [];
    if (statusFilter) items = items.filter((r) => r.status === statusFilter);
    if (marketFilter) items = items.filter((r) => r.market === marketFilter);
    if (triggerFilter) items = items.filter((r) => r.trigger_type === triggerFilter);
    if (criticalFilter === "true") items = items.filter((r) => r.critical);
    if (criticalFilter === "false") items = items.filter((r) => !r.critical);
    return items;
  }, [reportData, statusFilter, marketFilter, triggerFilter, criticalFilter]);

  const reportActiveFilters = [
    statusFilter ? `Status: ${statusFilter}` : "",
    marketFilter ? `Market: ${marketFilter}` : "",
    triggerFilter ? `Trigger: ${triggerFilter}` : "",
    criticalFilter === "true" ? "Critical only" : "",
    criticalFilter === "false" ? "Non-critical only" : "",
  ].filter(Boolean);

  const reportSummary = useMemo(() => {
    const countBy = (...keys: string[]) => reportItems.filter((r) => keys.includes(r.status)).length;
    return {
      onTime: countBy("success", "on_time"),
      late: countBy("delayed_ok", "delayed"),
      runLate: countBy("delayed_running"),
      missed: countBy("missed"),
      failed: countBy("failed"),
      running: countBy("in_progress"),
      recovered: countBy("recovered"),
      scheduled: countBy("scheduled"),
    };
  }, [reportItems]);

  const downloadReportCsv = useCallback(() => {
    if (!reportData) return;
    const esc = (v: any) => '"' + String(v == null ? "" : v).replace(/"/g, '""') + '"';
    const rows = [["Status", "Status Message", "Data Asset", "Market", "GDO", "Trigger Type", "Exp Start UTC", "Exp End UTC", "Act Start UTC", "Act End UTC", "Run Count", "Critical"]];
    reportItems.forEach((row) => {
      rows.push([
        row.status || "", row.status_message || "", row.data_asset || "", row.market || "", row.gdo || "",
        row.trigger_type || "", row.expected_start || row.expected_time || "", row.expected_end || "",
        row.actual_start || "", row.actual_end || "", row.run_count || 0, row.critical ? "Yes" : "No",
      ]);
    });
    const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "silver_status_" + date + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [reportData, reportItems, date]);

  return (
    <>
      <div id="dashboard">
        <Header
          dbLive={view?.db_live}
          right={
            <div className="refresh-info">
              <span id="last-refresh">{view ? `Last refresh: ${new Date(view.last_refresh || "").toUTCString().replace("GMT", "UTC")}` : "Loading..."}</span>
            </div>
          }
        />
        <Nav />

        <SummaryBar counts={counts} statusFilter={statusFilter} toggleCardFilter={toggleCardFilter} view={view} isRefreshing={isRefreshing} />

        <div className="view-mode-bar">
          <button className={`view-tab ${viewMode === "table" ? "active" : ""}`} onClick={() => setViewMode("table")}>&#9776; Table View</button>
          <button className={`view-tab ${viewMode === "hourly" ? "active" : ""}`} onClick={() => setViewMode("hourly")}>&#9200; Hourly View</button>
        </div>

        <div id="hv-panel" style={{ display: viewMode === "hourly" ? "" : "none", position: "relative" }}>
          {isRefreshing && (
            <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.75)", zIndex: 10, minHeight: 200 }}>
              <CircularProgress size={40} />
            </Box>
          )}
          <HourlyView date={date} onDateChange={setDate} dateMin={daysAgoIso(7)} dateMax={todayIso()} onOpenAsset={setOpenAsset} />
        </div>

        {viewMode === "table" && (
          <TableViewPanel
            visibleColumns={visibleColumns} sortKey={sortKey} sortDir={sortDir} onSort={onSort}
            error={error} view={view} isRefreshing={isRefreshing} pageItems={pageItems} page={page} rowsPerPage={rowsPerPage}
            setOpenAsset={setOpenAsset} filteredItems={filteredItems} setPage={setPage} setRowsPerPage={setRowsPerPage}
            search={search} setSearch={setSearch} appFilter={appFilter} setAppFilter={setAppFilter}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter} criticalFilter={criticalFilter}
            setCriticalFilter={setCriticalFilter} marketFilter={marketFilter} setMarketFilter={setMarketFilter}
            triggerFilter={triggerFilter} setTriggerFilter={setTriggerFilter} date={date} setDate={setDate}
            openReport={openReport} clearSort={clearSort} clearAllFilters={clearAllFilters}
            hidden={hidden} toggle={toggle} selectAll={selectAll} allChecked={allChecked}
          />
        )}
      </div>

      {openAsset && (
        <DetailPanel assetName={openAsset} onClose={() => setOpenAsset(null)} />
      )}

      <DialogModal
        isOpen={reportOpen}
        handleClose={() => setReportOpen(false)}
        width="lg"
        title={`Silver Status Report${reportData ? ` — ${reportData.day} ${date}` : ""}${reportActiveFilters.length ? ` (${reportActiveFilters.join(" · ")})` : ""}`}
        headerContent={
          <button onClick={downloadReportCsv} style={{ background: "rgba(255,255,255,0.12)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 4, padding: "6px 14px", cursor: "pointer", fontSize: ".8rem", marginTop: 8 }}>&#11015; Download CSV</button>
        }
        dialogContentProps={{ sx: { textAlign: "left" } }}
      >
        <ReportModalContent reportLoading={reportLoading} reportError={reportError} reportData={reportData} reportItems={reportItems} reportSummary={reportSummary} downloadReportCsv={downloadReportCsv} />
      </DialogModal>
    </>
  );
}
